import * as uuid from "uuid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import HeaderPanel from "../../layout/HeaderPanel";
import { IButtonProps, IContextualMenuItem, ImageFit, mergeStyleSets } from "@fluentui/react";
import FooterPanel from "../../layout/FooterPanel";
import { IconButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { DocumentPartTreeSelection, TreeViewDataItem } from "../DocumentPartTree";
import {
  fetchDocumentMetadata,
  fetchDocumentPartDetails,
  fetchParentsDocumentPart,
  fetchRecursiveParentsDocumentPart,
  fetchRootsDocumentPart,
} from "../../../api/document/DocumentsPart";
import { CreateDocumentPartModal } from "../CreateDocumentPartModal";
import { openDocumentById } from "../../../utils/helpers/OpenDocumentHelper";
import {
  checkDifferencesAsync,
  editDocumentMetadataAsync,
  exportWorkflowHistoryAsync,
  unlinkDocumentPartAsync,
} from "../../../redux/feature/document/document-slice";
import { ElaborationWrapper } from "../../coobjects/ElaborationWrapper";
import {
  INSERT_OBJECT_NOTIFICATION,
  MISSING_INHERITABLE_PARAMETERS_NOTIFICATION,
  REFRESH_DOCUMENT_PROPERTIES_NOTIFICATION,
} from "../../../utils/constants/notification";
import LoadingManager from "../../../common/loading/LoadingManager";
import {
  formateDateToPreferredLanguage,
  formatMessage,
  formatMessageWithParams,
  formatSehorseLastUpdateDate,
} from "../../../utils/helpers/LanguageHelper";
import { DocumentData, EditDocument } from "../../document/EditDocument";
import { DuplicateDocumentPanel } from "../../document/DuplicateDocumentPanel";
import NotificationManager from "../../../common/notification/NotificationManager";
import { DocumentPart, ElaborationRequest } from "../../../api/models";
import { DetachDocumentPanel } from "../../document/DetachDocumentPanel";
import { FindItem, useFilteredElaborations } from "../../generic/GenericElaborationSelector";
import { ButtonVariants } from "../../../styles/fluent/ButtonVariants";
import { FlatPathTreeItem } from "../../generic/FlatPathTreeExplorer";
import { DocumentOutline } from "../../document/DocumentOutline";
import { SkeletonLoader } from "../../../common/loading/SkeletonLoader";
import { UserFunction } from "../../../api/models/user-function";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import {
  selectCpmUsername,
  selectIsSendingIxbrl,
  selectIxbrlSettings,
  selectSeahorseDocumentLinked,
  selectServiceProvider,
  selectUserFunctions,
  setIxbrlSettings,
} from "../../../redux/feature/settings/configurations-slice";
import { isNotAuthorizedUser } from "../../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { DocumentPosition } from "../../../api/models/document-position";
import { DeleteDocumentHelper } from "../../../utils/helpers/DeleteDocumentsHelper";
import { DocumentPropertiesActionTypes } from "../DocumentPropertiesMessagesModal";
import { ServiceProvider } from "../../../api/models/service-provider";
import {
  fetchIxbrlSettings,
  fetchUpdateIxbrlSettings,
  hasNoDefaultServiceProvider,
  hasNoIxbrlSetting,
  sendDocumentToIXBRLAndRefresh,
} from "../../../pages/seahorse/SeahorseUtils";
import { checkIfPartExists } from "../../../office/WordDocumentPart";
import { LinkedDocumentInfo } from "../../../api/models/linked-document-info";
import { store } from "../../../redux/store/store";
import {
  DocPropKeys,
  getDocumentPartMetadataFromCustomXml,
  mapDocumentDataToDocumentPartMetadata,
  replaceCustomXmlMetadata,
  retrieveDocumentProp,
} from "../../../utils/helpers/CustomXmlHelper";
import { useCloneDocPart } from "./useCloneDocPart";
import { XmlDocumentMetadata } from "../../../api/models/xml-document-metadata";
import { getContentControlId } from "../../../utils/helpers/ContentControlHelper";
import { DocumentPartWithStatus } from "../../../api/models/document-part-with-status";
import {
  createElaborationRequestAsync,
  ElaborationMessage,
  elaborationStatusItemIsBlocking,
} from "../../../redux/feature/object/remote-elaborations-slice";
import { IxbrlSetting } from "../../../api/models/ixbrl-setting";
import { TgkDocumentElaborationRequest } from "../../../api/models/tgk-document-elaboration-request";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../../utils/constants/eventChannel";
import { AlignDocumentPanel } from "../../document/AlignDocumentPanel";
import { TreeItemRow } from "../TreeItemRow";
import { checkMissingInheritableParameters } from "../../../utils/helpers/InheritanceHelper";
import { CoObjectAdvanced } from "../../interfaces/CoObject";
import { ExportDocumentPanel } from "../../document/ExportDocumentPanel";
import { DimensionParameter } from "../../../api/models/dimension-parameter";
import { fetchDimensions } from "../../../api/object/Dimensions";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { restoreDefaultDocumentMetadata } from "../../../utils/helpers/DocumentPartMetadataHelper";
import {
  fetchInDesignLinkedDocument,
  sendDocumentToInDesign,
  setInDesignEnabled,
} from "../../../api/document/InDesign";
import { setRagIndexingEnabled } from "../../../api/document/RagIndexing";
import { getWorkflowStatus } from "../DocumentPartTreeUtils";
import {
  searchCleared,
  selectionCleared,
  selectIsOutlineSearchResultsVisible,
  selectOutlineSearchResults,
  selectOutlineSearchSelectedDocPartId,
} from "../../../redux/feature/cpm/outline-search-slice";
import { OutlineSearchFooter } from "../search/OutlineSearchFooter";

const loadingManagerKey = "DOCUMENT_PROPERTIES";
const SUBMITTED: string = "50";

export async function mapDocPartToDataItem(documentPart: DocumentPart): Promise<TreeViewDataItem> {
  return {
    key: documentPart.id,
    driveItemId: documentPart.driveItemId,
    text: documentPart.name,
    nodeType: documentPart.root ? "root" : "part",
    hasChildren: documentPart.hasChildren,
    expanded: documentPart.subparts?.length > 0,
    checked: false,
    selected: false,
    data: documentPart,
  };
}

type DocumentPropertiesPages = "outline" | "adddocpart" | "export" | "duplicate" | "edit" | "detach" | "align";

interface DocumentPropertiesState {
  key: number;
  partRoot: DocumentPart;
  initialDocumentData: {
    initialDocumentItem: FlatPathTreeItem;
    parents: DocumentPosition[];
  };
  itemSelected: DocumentPartTreeSelection | null;
  showMessagesModal: boolean;
  deletingPart: DocumentPart;
  action: DocumentPropertiesActionTypes;
  page: DocumentPropertiesPages;
  root?: boolean;
  currentOperationTreeItem: TreeViewDataItem;
  selectedAddOption: IContextualMenuItem;
  rootsWhereLinkedItems: TreeViewDataItem[];
  referringContentControlId: number;
  documentPartToAlign: DocumentPart;
  documentPartToSave: DocumentData;
  inDesignLinkedDocument?: LinkedDocumentInfo;
}

export interface DocumentPropertiesProps {
  idPartRoot: string;
  documentPart: DocumentPart;
}

const addOptions: IContextualMenuItem[] = [
  {
    id: "add_before_menuitem",
    key: "before",
    text: "cof.base.addbefore",
  },
  {
    id: "add_after_menuitem",
    key: "after",
    text: "cof.base.addafter",
  },
];

const initialState: DocumentPropertiesState = {
  key: 1,
  partRoot: null,
  initialDocumentData: null,
  itemSelected: null,
  showMessagesModal: false,
  action: null,
  page: "outline",
  currentOperationTreeItem: null,
  selectedAddOption: addOptions[0],
  rootsWhereLinkedItems: null,
  deletingPart: null,
  referringContentControlId: null,
  documentPartToAlign: null,
  documentPartToSave: null,
};

const notifyMissingInheritableParameters = async (missingInheritableParams: Array<DimensionParameter>) => {
  const errorMessages: ElaborationMessage[] = [];
  // complete the missing parameters with dimension details
  for (const missingParam of missingInheritableParams) {
    const dimensionsDetails = await fetchDimensions(missingParam.dbId, true);
    const missingInheritableParamDetails = dimensionsDetails.filter((d) =>
      missingParam.dimensions.some((value) => value.dimensionOid === d.oid)
    );
    console.log(`Missing dimensions for dbId ${missingParam.dbId}:`, missingInheritableParamDetails);
    missingInheritableParamDetails.forEach((dimension) => {
      errorMessages.push({
        text: formatMessage("cof.editdocument.title.inheritableparametersmissing"),
        details: formatMessageWithParams("cof.editdocument.message.detail.inheritableparametersmissing", {
          dimensionDesc: dimension.desc,
          dbId: missingParam.dbId,
        }),
        severity: "warning",
        traceId: undefined,
      });
    });
  }

  NotificationManager.addNotification({
    key: MISSING_INHERITABLE_PARAMETERS_NOTIFICATION,
    title: formatMessage("cof.editdocument.title.inheritableparametersmissing"),
    message: formatMessage("cof.editdocument.message.inheritableparametersmissing"),
    details: errorMessages,
    type: "warning",
    isTemporary: false,
  });
};

const findBlockingElaboration = (item: FindItem) =>
  item.elaborationType !== "SHEET_AND_AREA" && elaborationStatusItemIsBlocking(item);

export const DocumentProperties: React.FunctionComponent<DocumentPropertiesProps> = (props) => {
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const defaultServiceProvider: ServiceProvider = useAppSelector(selectServiceProvider);
  const seahorseDocumentLinked: LinkedDocumentInfo[] = useAppSelector(selectSeahorseDocumentLinked);
  const allIxbrlSettings: IxbrlSetting[] = useAppSelector(selectIxbrlSettings);
  const showSearchResults = useAppSelector(selectIsOutlineSearchResultsVisible);
  const outlineSearchResults = useAppSelector(selectOutlineSearchResults);
  const outlineSearchSelectedDocPartId = useAppSelector(selectOutlineSearchSelectedDocPartId);
  const [isCertifiedOrApprovedOrRejected, setIsCertifiedOrApprovedOrRejected] = useState(false);

  const { cloneDocPart } = useCloneDocPart(props.idPartRoot);
  let notAuthorizedUserForEditRoot = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_ROOT,
    userFunctions
  );
  let notAuthorizedUserForEditPart = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_EDIT_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForDuplicate = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DUPLICATE_DOCUMENTS,
    userFunctions
  );
  let notAuthorizedUserForNew = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_NEW_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForUnlink = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_UNLINK_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForDelete = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DELETE_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForDetach = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DETACH_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForSendCopyIXBRL = isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);

  const blockingElaboration = useFilteredElaborations({
    findFunction: findBlockingElaboration,
  });

  useEffect(() => {
    if (!blockingElaboration) void getInitialDocumentPartDetails();
  }, [blockingElaboration, props.idPartRoot]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(searchCleared());
    dispatch(selectionCleared());
  }, [dispatch, props.idPartRoot]);

  const [state, setState] = useState<DocumentPropertiesState>(initialState);
  const [documentPartWithStatus, setDocumentPartWithStatus] = useState<DocumentPartWithStatus[]>([]);
  const isSendingIxbrl = useAppSelector(selectIsSendingIxbrl);

  const getInitialDocumentPartDetails = async () => {
    // Step 1: Get parents (needed to compute rootIdToUse)
    const parents = await fetchRecursiveParentsDocumentPart(props.idPartRoot);
    const rootIdToUse = parents?.[0]?.idPadre ?? props.idPartRoot;

    // Reuse documentPart from CoRetrieveDocumentData — no need to re-fetch
    const thisDocumentPart = props.documentPart;

    // Step 2: Parallel — these calls are independent of each other
    const [treeRootItem, inDesignLinkedDocument, rootsWhereLinked] = await Promise.all([
      fetchDocumentPartDetails(rootIdToUse, {
        params: { notShowLoading: true },
      }),
      fetchInDesignLinkedDocument(props.idPartRoot, {
        params: { notShowLoading: true },
      }),
      thisDocumentPart?.multiLinked ? fetchRootsDocumentPart(props.idPartRoot) : Promise.resolve([]),
    ]);

    const rootsWhereLinkedItems = await Promise.all(rootsWhereLinked.map((r) => mapDocPartToDataItem(r)));
    LoadingManager.remove(loadingManagerKey);
    setState((s) => ({
      ...s,
      itemSelected: null,
      partRoot: thisDocumentPart,
      initialDocumentData: {
        initialDocumentItem: {
          text: treeRootItem.name,
          key: treeRootItem.id,
          document: treeRootItem,
          readPermission: true,
        },
        parents,
      },
      rootsWhereLinkedItems: rootsWhereLinkedItems,
      showMessagesModal: false,
      inDesignLinkedDocument: inDesignLinkedDocument,
    }));
  };

  function resetSelection() {
    LoadingManager.remove(loadingManagerKey);
    setState((s) => ({
      ...s,
      showMessagesModal: false,
      itemSelected: null,
      deletingPart: null,
      documentPartToAlign: null,
      page: "outline",
    }));
    void getInitialDocumentPartDetails();
  }

  async function unlinkDocumentPartAndReset(selectedPart: DocumentPart) {
    await unlinkDocumentPartAsync({
      docPartFatherId: state.partRoot.id,
      unlinkedDocPartId: selectedPart.id,
    });
    resetSelection();
  }

  function setPage(page: DocumentPropertiesPages) {
    setState((s) => ({ ...s, page: page }));
  }

  function haveSubmittedStatus(selectedItemKey: string, documentPartWithStatus: DocumentPartWithStatus[]) {
    return getWorkflowStatus(selectedItemKey, documentPartWithStatus)?.workflowStatusCode === SUBMITTED;
  }

  async function checkDifferences(item: TreeViewDataItem) {
    const tgkDocumentElaborationRequest: TgkDocumentElaborationRequest = {
      correlationId: uuid.v4(),
      clientId: store.getState().configurationState.clientId,
      userId: username,
      channelIdToNotify: props.idPartRoot ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      documentId: item.key,
      documentName: item.text,
      isOpenDocument: false,
      isAlignNeeded: true,
      typeElaborationRequest: "tgkDocumentElaborationRequest",
    };

    await checkDifferencesAsync(tgkDocumentElaborationRequest);
  }

  const getIconButtonMenuProps = (
    selectedItem: TreeViewDataItem,
    _itemHierarchicalIndex: string,
    documentPartWithStatus: DocumentPartWithStatus[]
  ) => {
    const isSonOfPartRoot = state.partRoot.id === selectedItem.data.fatherKey;
    const isDetachAvailable = isSonOfPartRoot && selectedItem.data.documentPart.multiLinked;
    const isLinkedTwiceOrMore = state.partRoot.subparts.filter((part) => part.id === selectedItem.key).length > 1;

    const options: IContextualMenuItem[] = [
      {
        id: "align_menuitem",
        key: "align",
        text: formatMessage("cof.base.runalignment"),
        onClick: () => {
          setState((s) => ({
            ...s,
            page: "align",
            documentPartToAlign: selectedItem.data,
          }));
        },
        hidden: !(
          selectedItem.data.documentPart?.hasChildren ??
          (selectedItem as any).documentPart?.hasChildren ??
          selectedItem.data.hasChildren
        ),
        disabled: notAuthorizedUserForEditRoot,
      },
      {
        id: "export_differences_menuitem",
        key: "export_differences",
        text: formatMessage("cof.base.exportdifferences"),
        onClick: async () => {
          try {
            await checkDifferences(selectedItem);
          } catch (error) {
            console.log(error);
          }
        },
      },
      {
        id: "unlink_menuitem",
        key: "unlink",
        text: formatMessage("cof.base.unlink"),
        onClick: () => {
          void unlinkDocumentPartAndReset(selectedItem.data.documentPart);
        },
        hidden: !(isSonOfPartRoot && (selectedItem.data.documentPart?.multiLinked || isLinkedTwiceOrMore)),
        disabled:
          notAuthorizedUserForUnlink ||
          (documentPartWithStatus?.length && haveSubmittedStatus(selectedItem.key, documentPartWithStatus)),
      },
      {
        id: "delete_menuitem",
        key: "delete",
        text: formatMessage("cof.base.delete"),
        onClick: () => {
          setState((s) => ({ ...s, deletingPart: selectedItem.data.documentPart }));
        },
        hidden: !isSonOfPartRoot,
        disabled:
          notAuthorizedUserForDelete ||
          (documentPartWithStatus?.length && haveSubmittedStatus(selectedItem.key, documentPartWithStatus)),
      },
      {
        id: "detach_menuitem",
        key: "detach",
        text: formatMessage("cof.base.detach"),
        onClick: () => {
          setState((s) => ({
            ...s,
            page: "detach",
            currentOperationTreeItem: selectedItem,
          }));
        },
        hidden: !isDetachAvailable,
        disabled:
          notAuthorizedUserForDetach ||
          (documentPartWithStatus?.length > 0 && haveSubmittedStatus(selectedItem.key, documentPartWithStatus)),
      },
      {
        id: "export_menuitem",
        key: "export",
        text: formatMessage("cof.base.export"),
        onClick: () => {
          setState((s) => ({
            ...s,
            page: "export",
            currentOperationTreeItem: selectedItem,
          }));
        },
      },
      {
        id: "exportObjects_menuitem",
        key: "exportObjects",
        text: formatMessage("cof.base.exportobjects"),
        onClick: async () => {
          try {
            NotificationManager.addNotification({
              key: INSERT_OBJECT_NOTIFICATION,
              message: formatMessage("cof.exportobjects.downloadRequested"),
              title: null,
              type: "success",
              closable: true,
              removeOnClose: true,
              isTemporary: true,
            });

            const correlationID = uuid.v4();
            const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);
            const clientID = store.getState().configurationState.clientId;

            const elaboration: ElaborationRequest = {
              correlationId: correlationID,
              clientId: clientID,
              documentId: documentId,
              channelIdToNotify: documentId || EVENT_CHANNEL_DEFAULT_TOPIC,
              userId: username,
              tgkElaborationItemList: [
                {
                  elaborationType: "EXPORT_OBJECT_LIST",
                  correlationId: correlationID,
                  documentId: documentId,
                  contentControlId: "",
                },
              ],
            };
            await createElaborationRequestAsync(elaboration);
          } catch (error) {
            console.log(error);
          }
        },
      },
    ];

    if (!selectedItem?.data?.documentPart && isCertifiedOrApprovedOrRejected) {
      options.push({
        id: "export_workflow_menuitem",
        key: "exportworkflowhistory",
        text: formatMessage("cof.base.export.workflow"),
        onClick: () => {
          const payload = {
            documentId: selectedItem.data.id,
            text: "export-submission-history-" + Date.now() + ".xlsx",
            hasChildren: false,
            expanded: true,
            checked: true,
            items: [],
            disabled: false,
          };
          void exportWorkflowHistoryAsync(payload);
        },
      });
    }

    if (!notAuthorizedUserForSendCopyIXBRL) {
      options.push({
        id: "sendcopyixbrl_menuitem",
        key: "sendcopyixbrl",
        text: formatMessage("cof.base.sendcopyixbrl"),
        onClick: () => {
          void sendDocumentToIXBRLAndRefresh(selectedItem.data, defaultServiceProvider.id);
        },
        hidden:
          selectedItem.key != state.initialDocumentData.initialDocumentItem.key ||
          hasNoIxbrlSetting(state.initialDocumentData.initialDocumentItem.key, allIxbrlSettings),
        disabled: hasNoDefaultServiceProvider(defaultServiceProvider) || isSendingIxbrl,
      });
      options.push({
        key: "updated",
        text: formatSehorseLastUpdateDate(seahorseDocumentLinked, selectedItem.data.driveItemId),
        hidden: !formatSehorseLastUpdateDate(seahorseDocumentLinked, selectedItem.data.driveItemId).length,
        disabled: true,
        className: "sentDateUpdate",
      });
    }

    const showSendToInDesign =
      selectedItem.key == state.initialDocumentData.initialDocumentItem.key &&
      (selectedItem.data.settings?.isInDesignEnabled || selectedItem.data.documentPart?.settings?.isInDesignEnabled);
    if (showSendToInDesign) {
      options.push({
        id: "sendtoindesign_menuitem",
        key: "sendtoindesign",
        text: formatMessage("cof.base.sendtoindesign"),
        onClick: () => {
          void sendDocumentToInDesign(selectedItem.data);
        },
      });
      options.push({
        key: "updated_indesign",
        text:
          formatMessage("cof.indesign.sent") +
          " " +
          formateDateToPreferredLanguage(
            state.inDesignLinkedDocument ? new Date(state.inDesignLinkedDocument.date) : undefined
          ),
        hidden: !state.inDesignLinkedDocument?.date,
        disabled: true,
        className: "sentDateUpdate",
      });
    }

    const visibleOptions = options.filter((opt) => !opt.hidden);

    if (visibleOptions.length === 0) {
      return null;
    }

    return {
      items: options,
      directionalHintFixed: true,
    };
  };

  const askUserToAlign = async (input: DocumentData) => {
    setState((s) => ({
      ...s,
      page: "align",
      documentPartToAlign: null,
      documentPartToSave: input,
    }));
  };

  const onSaveDocument = async (input: DocumentData) => {
    await saveDocument(false, input);
  };

  const saveDocument = async (mustAlignChildren: boolean, input?: DocumentData) => {
    LoadingManager.create(loadingManagerKey);

    const documentPartToSave = input ?? state.documentPartToSave;
    const isEditingActiveDocument = state.itemSelected.key === props.idPartRoot;
    const parents = await fetchParentsDocumentPart(documentPartToSave.documentId);

    const documentPart: DocumentPart = {
      name: documentPartToSave.name.replace(/\.docx$/, "") + ".docx",
      description: documentPartToSave.description,
      id: documentPartToSave.documentId,
      siteId: documentPartToSave.siteId,
      driveItemId: documentPartToSave.driveItemId,
      metadata: mapDocumentDataToDocumentPartMetadata(documentPartToSave),
      multiLinked:
        parents.length > 1 || documentPartToSave.multilinked || (parents.length == 1 && parents[0].multiLinked),
      hasChildren: documentPartToSave.hasChildren,
      unaligned: documentPartToSave.unaligned,
      unalignedSubParts: documentPartToSave.unalignedSubParts,
      settings: documentPartToSave.settings,
    };

    const tgkDocumentElaborationRequest: TgkDocumentElaborationRequest = {
      correlationId: uuid.v4(),
      clientId: store.getState().configurationState.clientId,
      userId: username,
      channelIdToNotify: props.idPartRoot ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      documentId: documentPart.id,
      documentPart: documentPart,
      isOpenDocument: isEditingActiveDocument,
      isAlignNeeded: mustAlignChildren,
      typeElaborationRequest: "tgkDocumentElaborationRequest",
    };

    const xmlDocumentMetadata: XmlDocumentMetadata = await editDocumentMetadataAsync(
      tgkDocumentElaborationRequest,
      mustAlignChildren
    );

    LoadingManager.remove(loadingManagerKey);

    if (isEditingActiveDocument) {
      await replaceCustomXmlMetadata(xmlDocumentMetadata);
      // update document settings
      await setInDesignEnabled(documentPartToSave.settings?.isInDesignEnabled ?? false);
      await setRagIndexingEnabled(documentPartToSave.settings?.isRagIndexingEnabled ?? false);
      if (documentPartToSave.settings?.isIxbrlEnabled !== undefined && documentPartToSave.documentId) {
        await fetchUpdateIxbrlSettings(
          documentPartToSave.documentId,
          documentPartToSave.settings?.isIxbrlEnabled ?? false
        );
        const updatedSettings = await fetchIxbrlSettings();
        store.dispatch(setIxbrlSettings(updatedSettings));
      }
    } else if (documentPartToSave.settings?.isIxbrlEnabled !== undefined && documentPartToSave.documentId) {
      await fetchUpdateIxbrlSettings(
        documentPartToSave.documentId,
        documentPartToSave.settings?.isIxbrlEnabled ?? false
      );
      const updatedSettings = await fetchIxbrlSettings();
      store.dispatch(setIxbrlSettings(updatedSettings));
    }

    resetSelection();
  };

  const classNames = mergeStyleSets({
    pageContainer: {
      position: "relative",
      paddingBottom: "80px",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      height: "100%",
    },

    contentWrap: {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      height: "100%",
    },
    content: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    },
  });

  const isRootSelected = useMemo(
    () => !state.itemSelected || state.itemSelected.key === state.partRoot.id,
    [state.itemSelected, state.partRoot]
  );

  const isMainDocument = useMemo(() => {
    return !state.itemSelected || state.itemSelected.key === state.initialDocumentData.initialDocumentItem.document.id;
  }, [state.initialDocumentData, state.itemSelected]);

  const isItemSelectedSonOfPartRoot = () => {
    // If the selected item is expanded, an extra "0" string is added to oidPath, so we can't go with the last index.
    // eg. ["rootId", "partId"] if not expanded, ["rootId", "partId", "0"] if expanded
    // We need to find the index of the selected item in the hierarchy
    const itemSelectedOidPathIndex = state.itemSelected.oidPath.findIndex(
      (parent) => parent === state.itemSelected.key
    );
    return state.itemSelected.oidPath[itemSelectedOidPathIndex - 1] === state.partRoot.id;
  };

  const addButtonProps: IButtonProps = {
    disabled:
      !state.itemSelected?.data.hasReadPermissions ||
      !(isItemSelectedSonOfPartRoot() || isRootSelected) ||
      notAuthorizedUserForNew ||
      (documentPartWithStatus?.length > 0 && haveSubmittedStatus(state.partRoot.id, documentPartWithStatus)),
    id: "add",
    onClick: () => setState((s) => ({ ...s, page: "adddocpart" })),
    text: formatMessage("cof.base.add").toUpperCase(),
    menuProps: isRootSelected
      ? null
      : {
          items: addOptions.map((o) => ({ ...o, text: formatMessage(o.text) })),
          isBeakVisible: false,
          onItemClick: (_, opt) => {
            getContentControlId(state.itemSelected?.item.data.documentPart?.id)
              .then((ccId) => {
                checkIfPartExists(ccId);
                setState((s) => ({
                  ...s,
                  page: "adddocpart",
                  selectedAddOption: opt,
                  referringContentControlId: ccId,
                }));
              })
              .catch((e) => {
                NotificationManager.addNotification({
                  title: e,
                  type: "error",
                  message: formatMessage("cof.objectsource.documentpart.selectednotexisting"),
                });
              });
          },
        },
  };

  const editButtonProps: IButtonProps = {
    id: "edit",
    disabled:
      !state.itemSelected?.data.hasReadPermissions || haveSubmittedStatus(state.partRoot.id, documentPartWithStatus),
    text: formatMessage("cof.base.edit"),
    onClick: () => setState((s) => ({ ...s, page: "edit" })),
  };

  const duplicateButtonProps: IButtonProps = {
    id: isMainDocument ? "duplicate" : "clone",
    disabled:
      !state.itemSelected?.data.hasReadPermissions ||
      notAuthorizedUserForDuplicate ||
      (!isRootSelected && !isItemSelectedSonOfPartRoot()) ||
      (isRootSelected && !isMainDocument) ||
      (!isMainDocument && haveSubmittedStatus(state.partRoot.id, documentPartWithStatus)),

    text: isMainDocument ? formatMessage("cof.base.rollforward") : formatMessage("cof.base.clone"),
    onClick: () => {
      if (!isMainDocument) {
        void cloneDocPart(state.itemSelected);
        return;
      }

      setState((s) => ({
        ...s,
        page: "duplicate",
      }));
    },
  };

  function onItemClick(item: DocumentPartTreeSelection) {
    setState((s) => ({ ...s, itemSelected: item }));
  }

  const getDocumentMetadata = async (documentId: string, isActiveDocument: boolean): Promise<DocumentPartMetadata> => {
    if (isActiveDocument) {
      return await getDocumentPartMetadataFromCustomXml();
    }

    const documentMetadata = await fetchDocumentMetadata(documentId, false);
    if (documentMetadata) {
      return documentMetadata;
    }

    return restoreDefaultDocumentMetadata(documentId);
  };

  //TODO DUPLICATION
  const [paths, setPaths] = useState<Map<string, string>>(new Map());
  const setPathForItem = useCallback((itemId: string, path: string) => setPaths((m) => m.set(itemId, path)), [paths]);
  const getPathForItem = useCallback((itemId: string) => paths.get(itemId), [paths]);

  const outlineSearchSelectedRow = outlineSearchSelectedDocPartId
    ? outlineSearchResults.find((r) => r.documentPart.id === outlineSearchSelectedDocPartId)
    : undefined;
  const searchEditDisabled = outlineSearchSelectedRow?.hasReadPermissions !== true;
  const onSearchEdit = () => {
    if (!outlineSearchSelectedRow) return;
    const docPart = outlineSearchSelectedRow.documentPart;
    const fakeItem: TreeViewDataItem = {
      text: docPart.name,
      key: docPart.id,
      hasChildren: docPart.hasChildren ?? false,
      nodeType: "part",
      data: docPart,
      hasReadPermissions: outlineSearchSelectedRow.hasReadPermissions,
    };
    const itemSelected: DocumentPartTreeSelection = {
      item: fakeItem,
      IdRoot: docPart.rootId,
      name: docPart.name,
      key: docPart.id,
      order: 0,
      oidPath: [docPart.rootId, docPart.id],
      data: fakeItem,
    };
    setState((s) => ({ ...s, itemSelected, page: "edit" }));
  };

  if (state.page === "outline")
    return (
      <ElaborationWrapper
        elaborationItem={blockingElaboration}
        notificationKey={REFRESH_DOCUMENT_PROPERTIES_NOTIFICATION}
      >
        <>
          <HeaderPanel applyPadding>
            {formatMessageWithParams("cof.file.open.outline.title.documentoutline", {
              documentName: state.partRoot?.name?.replace(/\.docx$/, ""),
            })}
          </HeaderPanel>
          <div
            className="ms-Grid"
            dir="ltr"
            style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
          >
            <div className="ms-Grid-row" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div
                className="ms-Grid-col ms-sm12 ms-md12 ms-lg12"
                style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
              >
                {state.initialDocumentData && state.partRoot ? (
                  <div className={classNames.pageContainer}>
                    <div className={classNames.contentWrap}>
                      <div className={classNames.content}>
                        <DocumentOutline
                          onItemClick={onItemClick}
                          isOutlineRibbon={true}
                          enableFilter={true}
                          selectedTreeItem={state.initialDocumentData.initialDocumentItem}
                          idPartRoot={props.idPartRoot}
                          getIconButtonOutlineRibbon={(item, itemHierarchicalIndex, documentPartWithStatus) =>
                            getIconButtonMenuProps(item, itemHierarchicalIndex, documentPartWithStatus)
                          }
                          parents={state.initialDocumentData.parents}
                          documentPartWithStatus={documentPartWithStatus}
                          setDocumentPartWithStatus={setDocumentPartWithStatus}
                          onAlignDocument={(selectedItem) => {
                            setState((s) => ({
                              ...s,
                              page: "align",
                              documentPartToAlign: selectedItem.document,
                            }));
                          }}
                          siteId={props.documentPart.siteId}
                          driveItemId={props.documentPart.driveItemId}
                          setIsCertifiedOrApprovedOrRejected={setIsCertifiedOrApprovedOrRejected}
                        />
                        {state.rootsWhereLinkedItems?.length ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "1rem",
                            }}
                          >
                            {formatMessage("cof.outline.partcontainedin")}
                            {state.rootsWhereLinkedItems.map((r) => (
                              <TreeItemRow
                                item={r}
                                itemHierarchicalIndex=""
                                isSelected={false}
                                key={(r.data as DocumentPart).id + "ROOT"}
                                onClick={(event) => {
                                  if (event.detail === 2) {
                                    //double click
                                    openDocumentById(r.key);
                                  }
                                  event.preventDefault();
                                }}
                                pathInfo={{ setPathForItem, getPathForItem }}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={classNames.pageContainer}>
                    <div className={classNames.contentWrap}>
                      <div className={classNames.content}>
                        <div style={{ paddingTop: "0.5rem" }}>
                          <SkeletonLoader />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <FooterPanel>
              {showSearchResults ? (
                <OutlineSearchFooter disabled={searchEditDisabled} onClick={onSearchEdit} />
              ) : (
                <>
                  <div
                    className="ms-hiddenSm"
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "5px",
                    }}
                  >
                    <PrimaryButton {...editButtonProps} className={ButtonVariants.primary} />
                    <PrimaryButton {...duplicateButtonProps} className={ButtonVariants.primary} />
                    <PrimaryButton {...addButtonProps} className={ButtonVariants.primary} />
                  </div>
                  <div
                    className="ms-hiddenMdUp"
                    style={{
                      gap: ".25rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      width: "100%",
                    }}
                  >
                    <IconButton
                      title={formatMessage("cof.base.edit")}
                      {...editButtonProps}
                      styles={{
                        icon: { height: "1.5rem" },
                        root: {
                          backgroundColor: editButtonProps.disabled ? "#D0D0D0" : null,
                          opacity: editButtonProps.disabled ? 0.5 : 1,
                        },
                      }}
                      iconProps={{
                        imageProps: {
                          src: "/assets/edit.svg",
                          imageFit: ImageFit.contain,
                          width: "1.5rem",
                          height: "1.5rem",
                        },
                      }}
                    />
                    <IconButton
                      title={duplicateButtonProps.text}
                      {...duplicateButtonProps}
                      styles={{
                        icon: { height: "1.5rem" },
                        root: {
                          backgroundColor: duplicateButtonProps.disabled ? "#D0D0D0" : null,
                          opacity: duplicateButtonProps.disabled ? 0.5 : 1,
                        },
                      }}
                      iconProps={{
                        imageProps: {
                          src: isRootSelected ? "/assets/roll-forward.svg" : "/assets/duplicate.svg",
                          imageFit: ImageFit.contain,
                          width: "1.5rem",
                          height: "1.5rem",
                        },
                      }}
                    />
                    <IconButton
                      title={formatMessage("cof.base.add")}
                      {...addButtonProps}
                      styles={{
                        icon: { height: "1.5rem" },
                        root: {
                          backgroundColor: addButtonProps.disabled ? "#D0D0D0" : null,
                          opacity: addButtonProps.disabled ? 0.5 : 1,
                        },
                      }}
                      iconProps={{
                        imageProps: {
                          src: "/assets/add.svg",
                          imageFit: ImageFit.contain,
                          width: "1.5rem",
                          height: "1.5rem",
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </FooterPanel>
            <DeleteDocumentHelper
              partRoot={state.partRoot}
              currentDocumentPartId={props.idPartRoot}
              onCancelDelete={() => resetSelection()}
              deleteItem={state.deletingPart}
            />
          </div>
        </>
      </ElaborationWrapper>
    );
  if (state.page === "adddocpart")
    return (
      <CreateDocumentPartModal
        fatherPartId={state.partRoot.id}
        documentPart={props.documentPart}
        documentInsertionPoint={{
          referenceContentControlId: state.referringContentControlId,
          //TODO broken by the new pantarei ouline calls. To be resolved checking the mapper
          referenceDocumentId: state.itemSelected?.item.data.documentPart?.id ?? state.partRoot.id,
          position: state.selectedAddOption.key as "before" | "after",
        }}
        isModalOpen={true}
        onHide={() => resetSelection()}
        loadingManagerKey={loadingManagerKey}
      />
    );
  if (state.page === "edit")
    return (
      <>
        <HeaderPanel applyPadding>
          {state.itemSelected?.data.data.multiLinked ? (
            <>
              {formatMessage("cof.documentmanagement.outline.edit.editlinkedpart")}
              <span
                style={{
                  marginLeft: "0.5rem",
                  fontSize: "25px",
                }}
                className={"k-icon k-i-link-vertical k-rotate-45"}
              />
            </>
          ) : (
            formatMessage("cof.documentmanagement.outline.edit.editdocument")
          )}
        </HeaderPanel>
        <EditDocument
          onLastBackButton={() => setPage("outline")}
          isEdit={true}
          readonly={
            (notAuthorizedUserForEditRoot && isMainDocument) || (notAuthorizedUserForEditPart && !isMainDocument)
          }
          fatherPartId={state.partRoot.id !== state.itemSelected?.key ? state.partRoot.id : null}
          getDocumentPartDetails={async () => {
            const documentPart = await fetchDocumentPartDetails(state.itemSelected?.key);
            const documentMetadata = await getDocumentMetadata(state.itemSelected?.key, isRootSelected);

            let inheritableParameters: DimensionParameter[];
            if (!documentPart.root && !documentPart.multiLinked) {
              inheritableParameters = documentMetadata.inheritableParametersMetadata;
              const missingInheritableParams = checkMissingInheritableParameters(
                documentMetadata.parametersMetadata,
                documentMetadata.inheritableParametersMetadata
              );

              if (missingInheritableParams && missingInheritableParams.length > 0) {
                await notifyMissingInheritableParameters(missingInheritableParams);
              }
            }

            const advanced: CoObjectAdvanced = {
              logicId: documentMetadata.logicId,
              language: documentMetadata.language,
              formatMetadata: documentMetadata.formatMetadata,
              formatImage: documentMetadata.formatImage,
              templateInfo: documentMetadata.templateInfo,
              inDesignLink: documentMetadata.inDesignLink,
            };

            return {
              documentData: {
                description: documentPart.description,
                name: documentPart.name,
                root: documentPart.root,
                multilinked: documentPart.multiLinked,
                hasChildren: documentPart.hasChildren,
                path: documentPart.path,
                documentId: documentPart.id,
                siteId: documentPart.siteId,
                driveItemId: documentPart.driveItemId,
                advanced,
                language: documentMetadata.language,
                parameters: documentMetadata.parametersMetadata,
                inheritableParameters: inheritableParameters,
                unaligned: documentPart.unaligned,
                unalignedSubParts: documentPart.unalignedSubParts,
                settings: documentPart.settings,
              },
            };
          }}
          onSave={(input, mustAlignChildren) => (mustAlignChildren ? askUserToAlign(input) : onSaveDocument(input))}
        />
      </>
    );
  if (state.page === "export")
    return (
      <ExportDocumentPanel
        documentId={state.currentOperationTreeItem.key}
        onCloseModal={() => resetSelection()}
        channelIdToNotify={props.idPartRoot}
      />
    );
  if (state.page === "detach")
    return (
      <DetachDocumentPanel
        documentId={state.currentOperationTreeItem.key}
        fatherDocumentId={props.idPartRoot}
        onCloseModal={() => resetSelection()}
        channelIdToNotify={props.idPartRoot}
      />
    );
  if (state.page === "duplicate")
    return (
      <DuplicateDocumentPanel
        documentId={state.itemSelected.key}
        documentName={state.itemSelected.name}
        onCloseModal={() => resetSelection()}
        channelIdToNotify={props.idPartRoot}
      />
    );
  if (state.page === "align") {
    return (
      <AlignDocumentPanel
        documentPartToAlign={state.documentPartToAlign}
        documentPartToSave={state.documentPartToSave}
        onCloseModal={() => resetSelection()}
        saveDocument={(input) => saveDocument(true, input)}
        channelIdToNotify={props.idPartRoot}
      />
    );
  }
};
